import _ from 'underscore';
import { startsWith } from 'underscore.string';
import { generateGuid } from '@bingads-webui-universal/primitive-utilities';
import { CustomColumnConfig } from '../types';

/**
 * Custom columns are stored by GUID in preferences service. When referenced in
 * OData calls and within the grid and its plugins, the custom column is referred-to
 * by its ID prefixed with 'PerformanceMetrics/CustomColumn'
 */
export const ODATA_PREFIX = 'PerformanceMetrics/CustomColumn';

/**
 * Grid that supports custom column in MCC level.
 */
export const MCCCUSTOMCOLUMNGRID = 'PerformanceGrid';

/**
 * MCC preference name
 */
export const MCCPREFERENCENAME = 'MCCGrid';

/**
 * single account preference name
 */
export const SINGLEACCOUNTPREFERENCENAME = 'SingleAccountGrid';

/**
 * @param {string} customColumnId GUID string without dashes (see: generateSafeGuid())
 * or a custom column grid key (will passively refuse to recursively prefix IDs).
 * @returns {string} GUID prefixed with custom column grid key
 */
export function customColumnGridKeyFromId(customColumnId: string): string {
  if (!_.isString(customColumnId) || customColumnId.length < 1) {
    throw new Error('customColumnId is required to generate odataId');
  }

  if (startsWith(customColumnId, ODATA_PREFIX)) {
    return customColumnId;
  }

  return `${ODATA_PREFIX}${customColumnId}`;
}

/**
 * @param {string} idWithOrWithoutPrefix Custom Column grid key or output of generateSafeGuid()
 * @returns {string} GUID-only part of ID with prefix removed (if exists)
 */
export function parseIdFromGridKey(idWithOrWithoutPrefix: string): string {
  if (!_.isString(idWithOrWithoutPrefix) || idWithOrWithoutPrefix.length < 1) {
    throw new Error('non-empty string argument required to parse odataId');
  }

  return _.last(String(idWithOrWithoutPrefix).split(ODATA_PREFIX)) || '';
}

/**
 * Middle-tier endpoint does not support dashes in IDs
 * @returns {string} GUID with dashes removed
 */
export function generateSafeGuid(): string {
  return generateGuid().replace(/[-]+/g, '');
}

/**
 * @returns {string} A new GUID with custom column prefix attached
 */
export function generateCustomColumnGridKey(): string {
  return customColumnGridKeyFromId(generateSafeGuid());
}

/**
 * Generate a JSON object for gridDataParameters.CustomColumns from
 * custom column configuration object.
 * @param {CustomColumnConfig} customColumn - object representing custom column config
 * @param {boolean} strict - if true, undefined values will trigger error
 * @returns {Object} - properly-formatted CustomColumn for GDP
 */
export function customColumnForGridDataParams(customColumn: CustomColumnConfig, strict = true): object {
  if (strict) {
    _.each(['id', 'name', 'expression', 'format'], (requiredKey) => {
      if (!_.has(customColumn, requiredKey)) {
        throw new Error(`Missing custom column attribute "${requiredKey}"`);
      }
    });
  }

  return {
    Id: customColumn.id,
    Name: customColumn.name,
    CustomColumnExpression: _.isArray(customColumn.expression)
      ? _.compact(customColumn.expression).join(' ')
      : customColumn.expression,
    OutputFormat: customColumn.format,
  };
}

/**
 * Returns name used to create preference key.
 * It's shared across all grid except performance grid which is in MCC level.
 * @param {string} name - grid name
 * @returns {string} - preference name
 */
export function getCustomColumnPreferenceName(name: string): string {
  if (name === MCCCUSTOMCOLUMNGRID) {
    return MCCPREFERENCENAME;
  }

  return SINGLEACCOUNTPREFERENCENAME;
}
